import React, { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ColumnReferenceCell,
  ColumnReferenceColumnCell,
  ColumnReferenceHead,
  ExampleTableCell,
  ExampleTableHead,
  ExampleTableRow,
  GuideBackdrop,
  GuideBody,
  GuideCloseButton,
  GuideFooter,
  GuideHeader,
  GuideKickerLabel,
  GuideKickerRow,
  GuideModal,
  GuidePrimaryButton,
  GuideSubtitle,
  GuideTitle,
  InfoCallout,
  InfoCalloutContent,
  InfoCalloutIcon,
  InfoCalloutList,
  InfoCalloutText,
  InfoCalloutTitle,
  MoreButton,
  MoreInlineCode,
  MorePanel,
  ParticipantsGuideSection,
  ParticipantsSectionTitle,
  ReportAccessColumnReferenceTable,
  ReportAccessExampleTable,
  RequiredYes,
  SectionDescription,
  VisualLabel,
} from './ImportModal.styles';
import CheckMark from '@/assets/icons/checkMark.svg?react';
import CloseMark from '@/assets/icons/closeMark.svg?react';

interface IImportReportAccessHelpModal {
  active: boolean;
  onClose: () => void;
}

const COLUMN_ROW_IDS = ['department', 'email', 'name', 'phone'] as const;
const REQUIRED_COLUMNS = new Set<typeof COLUMN_ROW_IDS[number]>(['department', 'email', 'name']);
const FILE_EXAMPLE_ROW_IDS = ['jane', 'mark'] as const;
const FILE_EXAMPLE_FIELDS = ['department', 'email', 'name', 'phone'] as const;
const INFO_CALLOUT_IDS = ['exactDepartments', 'accessGranted'] as const;

export const ImportReportAccessHelpModal: FC<IImportReportAccessHelpModal> = ({
  active,
  onClose,
}) => {
  const { t } = useTranslation();
  const [showMore, setShowMore] = useState(false);

  if (!active) return null;

  return (
    <>
      <GuideBackdrop onClick={onClose} />
      <GuideModal
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-access-guide-title"
        data-testid="import-report-access-help-content"
      >
        <GuideHeader>
          <GuideKickerRow>
            <GuideKickerLabel>{t('imports.reportAccessHelp.kicker')}</GuideKickerLabel>
          </GuideKickerRow>
          <GuideCloseButton type="button" aria-label={t('imports.close')} onClick={onClose}>
            <CloseMark />
          </GuideCloseButton>
          <GuideTitle id="report-access-guide-title">
            {t('imports.reportAccessHelp.guideTitle')}
          </GuideTitle>
          <GuideSubtitle>{t('imports.reportAccessHelp.guideSubtitle')}</GuideSubtitle>
        </GuideHeader>

        <GuideBody>
          <ParticipantsGuideSection>
            <ParticipantsSectionTitle>
              {t('imports.reportAccessHelp.columnReference.title')}
            </ParticipantsSectionTitle>
            <SectionDescription>
              <Trans
                i18nKey="imports.reportAccessHelp.columnReference.intro"
                components={[
                  <MoreInlineCode key="department" />,
                  <MoreInlineCode key="email" />,
                  <MoreInlineCode key="name" />,
                  <MoreInlineCode key="phone" />,
                ]}
              />
            </SectionDescription>

            <ReportAccessColumnReferenceTable>
              <thead>
                <tr>
                  <ColumnReferenceHead>
                    {t('imports.reportAccessHelp.columnReference.headers.column')}
                  </ColumnReferenceHead>
                  <ColumnReferenceHead>
                    {t('imports.reportAccessHelp.columnReference.headers.required')}
                  </ColumnReferenceHead>
                  <ColumnReferenceHead>
                    {t('imports.reportAccessHelp.columnReference.headers.notes')}
                  </ColumnReferenceHead>
                </tr>
              </thead>
              <tbody>
                {COLUMN_ROW_IDS.map(rowId => (
                  <tr key={rowId}>
                    <ColumnReferenceColumnCell>
                      {t(`imports.reportAccessHelp.columnReference.rows.${rowId}.column`)}
                    </ColumnReferenceColumnCell>
                    <ColumnReferenceCell>
                      {REQUIRED_COLUMNS.has(rowId) ? (
                        <RequiredYes>
                          {t('imports.reportAccessHelp.columnReference.requiredYes')}
                        </RequiredYes>
                      ) : (
                        t('imports.reportAccessHelp.columnReference.requiredNo')
                      )}
                    </ColumnReferenceCell>
                    <ColumnReferenceCell>
                      {t(`imports.reportAccessHelp.columnReference.rows.${rowId}.notes`)}
                    </ColumnReferenceCell>
                  </tr>
                ))}
              </tbody>
            </ReportAccessColumnReferenceTable>

            <VisualLabel>{t('imports.reportAccessHelp.fileExample.title')}</VisualLabel>
            <ReportAccessExampleTable>
              <thead>
                <ExampleTableRow>
                  {FILE_EXAMPLE_FIELDS.map(field => (
                    <ExampleTableHead key={field}>
                      {t(`imports.reportAccessHelp.fileExample.headers.${field}`)}
                    </ExampleTableHead>
                  ))}
                </ExampleTableRow>
              </thead>
              <tbody>
                {FILE_EXAMPLE_ROW_IDS.map(rowId => (
                  <ExampleTableRow key={rowId}>
                    {FILE_EXAMPLE_FIELDS.map(field => (
                      <ExampleTableCell key={`${rowId}-${field}`}>
                        {t(`imports.reportAccessHelp.fileExample.rows.${rowId}.${field}`, {
                          defaultValue: '',
                        })}
                      </ExampleTableCell>
                    ))}
                  </ExampleTableRow>
                ))}
              </tbody>
            </ReportAccessExampleTable>

            <MoreButton
              type="button"
              aria-expanded={showMore}
              onClick={() => setShowMore(value => !value)}
            >
              <span>{showMore ? '−' : '+'}</span>
              {showMore
                ? t('imports.reportingGroupsHelp.hide')
                : t('imports.reportAccessHelp.showMore')}
            </MoreButton>
            {showMore ? (
              <MorePanel>
                <InfoCalloutList>
                  {INFO_CALLOUT_IDS.map(calloutId => (
                    <InfoCallout key={calloutId}>
                      <InfoCalloutIcon aria-hidden="true">
                        <CheckMark />
                      </InfoCalloutIcon>
                      <InfoCalloutContent>
                        <InfoCalloutTitle>
                          {t(`imports.reportAccessHelp.more.${calloutId}.title`)}
                        </InfoCalloutTitle>
                        <InfoCalloutText>
                          {t(`imports.reportAccessHelp.more.${calloutId}.text`)}
                        </InfoCalloutText>
                      </InfoCalloutContent>
                    </InfoCallout>
                  ))}
                </InfoCalloutList>
              </MorePanel>
            ) : null}
          </ParticipantsGuideSection>
        </GuideBody>

        <GuideFooter>
          <GuidePrimaryButton type="button" onClick={onClose}>
            {t('imports.reportingGroupsHelp.gotIt')}
          </GuidePrimaryButton>
        </GuideFooter>
      </GuideModal>
    </>
  );
};

export default ImportReportAccessHelpModal;
